import { FileText } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import type { ComplianceDocumentView } from '../../lib/compliance-documents';
import { useToast } from '../../lib/use-toast';
import { getDriverDocumentDownloadUrl } from './drivers.api';
import { DriverDocumentRow } from './DriverDocumentRow';
import { DriverDocumentUploadModal } from './DriverDocumentUploadModal';
import type { UploadDriverDocumentInput } from './use-driver-documents';

interface DriverDocumentsProps {
  driverId: string;
  documents: ComplianceDocumentView[];
  canManage: boolean;
  onUpload: (input: UploadDriverDocumentInput, isRenewal: boolean) => Promise<boolean>;
}

/** A driver's compliance documents (gitaumoses4/chukua#17): one row per active document type, each showing its current document or "missing" — attach and renew only, never edit or remove. */
export function DriverDocuments({ driverId, documents, canManage, onUpload }: DriverDocumentsProps) {
  const [acting, setActing] = useState<ComplianceDocumentView | null>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  async function handleUpload(input: UploadDriverDocumentInput, isRenewal: boolean): Promise<boolean> {
    setUploading(true);
    try {
      return await onUpload(input, isRenewal);
    } finally {
      setUploading(false);
    }
  }

  async function download(documentId: string) {
    try {
      const { url } = await getDriverDocumentDownloadUrl(driverId, documentId);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error("Couldn't open the document");
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Documents</h2>
      {documents.length === 0 && (
        <EmptyState icon={FileText} title="No document types yet" description="Add document types for drivers in fleet reference data." />
      )}
      {documents.length > 0 && (
        <ul className="flex flex-col gap-3">
          {documents.map((document) => (
            <DriverDocumentRow key={document.documentTypeId} document={document} canManage={canManage} onDownload={download} onAct={setActing} />
          ))}
        </ul>
      )}
      <DriverDocumentUploadModal
        key={acting?.documentTypeId ?? 'closed'}
        open={acting !== null}
        documentType={acting}
        uploading={uploading}
        onUpload={async (input, isRenewal) => {
          const uploaded = await handleUpload(input, isRenewal);
          if (uploaded) setActing(null);
          return uploaded;
        }}
        onClose={() => setActing(null)}
      />
    </section>
  );
}
